import React, { useState } from 'react';
import { Button, Grid, IconButton, Radio, TextField, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, OtherPaymentMethodData } from '../api';
import { ApiError } from '../api/core/ApiError';
import { useSafeExecute } from '../common/apiNotifications/safeExecute';

type OtherPaymentMethodsSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (open: boolean) => void;
};

type PaymentMethodRow = {
  key: string;
  id?: string;
  label: string;
};

const rowsFromAccount = (account: AccountData): PaymentMethodRow[] =>
  (account?.other_payment_methods ?? []).map((method: OtherPaymentMethodData) => ({
    key: method.id ?? method.label,
    id: method.id,
    label: method.label,
  }));

export const OtherPaymentMethodsSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: OtherPaymentMethodsSettingsProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const [rows, setRows] = useState<PaymentMethodRow[]>(() => rowsFromAccount(account));
  const [defaultKey, setDefaultKey] = useState<string>(account?.default_other_payment_method_id ?? '');
  const [newRowCounter, setNewRowCounter] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const trimmedLabels = rows.map((row) => row.label.trim().toLowerCase());
  const isBlank = (row: PaymentMethodRow) => row.label.trim() === '';
  const isDuplicate = (row: PaymentMethodRow) =>
    trimmedLabels.filter((label) => label === row.label.trim().toLowerCase()).length > 1;
  const hasBlankLabel = trimmedLabels.some((label) => label === '');
  const hasDuplicateLabel = new Set(trimmedLabels).size !== trimmedLabels.length;

  const addRow = () => {
    setRows((current) => [...current, { key: `new-${newRowCounter}`, label: '' }]);
    setNewRowCounter((current) => current + 1);
  };

  const updateLabel = (key: string, label: string) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, label } : row)));

  const deleteRow = (key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
    if (defaultKey === key) setDefaultKey('');
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const updatedAccount: AccountData = await safeExecute({
        apiCall: () =>
          AccountService.updateOtherPaymentMethods({
            other_payment_methods: rows.map((row) => ({ id: row.id, label: row.label.trim() })),
            default_other_payment_method_id: rows.find((row) => row.key === defaultKey)?.id || undefined,
          }),
        successMessage: t('accountSettings.otherPaymentMethods.saved'),
        onErrorMessageGenerator: (error: ApiError) => {
          const detail = error.body?.detail;
          return typeof detail === 'string' ? detail : t('api.error.default');
        },
      });
      await updateLocalAccount(updatedAccount);
      setRows(rowsFromAccount(updatedAccount));
      setDefaultKey(updatedAccount?.default_other_payment_method_id ?? '');
      setIsUpdatedSnackOpen(true);
    } catch {
      // safeExecute already surfaced the failure; keep the edits so the admin can retry.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" gap={2} maxWidth={600}>
      <Grid container flexDirection="column">
        <Typography variant="h6">{t('accountSettings.otherPaymentMethods.title')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('accountSettings.otherPaymentMethods.description')}
        </Typography>
      </Grid>
      {rows.length === 0 && (
        <Typography color="text.secondary">{t('accountSettings.otherPaymentMethods.empty')}</Typography>
      )}
      {rows.map((row) => (
        <Grid key={row.key} container alignItems="center" gap={1} wrap="nowrap">
          <Tooltip
            title={row.id ? '' : t('accountSettings.otherPaymentMethods.saveFirst')}
            arrow
            disableHoverListener={!!row.id}>
            {/* A disabled control swallows pointer events, so the tooltip needs a live wrapper. */}
            <span>
              <Radio
                name="default-method"
                disabled={!row.id}
                checked={defaultKey === row.key}
                onChange={() => setDefaultKey(row.key)}
                inputProps={{ 'aria-label': t('accountSettings.otherPaymentMethods.defaultRadio') }}
              />
            </span>
          </Tooltip>
          <TextField
            size="small"
            fullWidth
            label={t('accountSettings.otherPaymentMethods.label')}
            value={row.label}
            onChange={(event) => updateLabel(row.key, event.target.value)}
            error={isBlank(row) || isDuplicate(row)}
            helperText={
              isBlank(row)
                ? t('accountSettings.otherPaymentMethods.labelRequired')
                : isDuplicate(row)
                ? t('accountSettings.otherPaymentMethods.labelDuplicate')
                : undefined
            }
          />
          <IconButton aria-label={t('delete')} onClick={() => deleteRow(row.key)}>
            <Delete />
          </IconButton>
        </Grid>
      ))}
      {rows.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {t('accountSettings.otherPaymentMethods.defaultHint')}
        </Typography>
      )}
      <Grid container justifyContent="space-between" alignItems="center">
        <Button startIcon={<Add />} onClick={addRow}>
          {t('accountSettings.otherPaymentMethods.add')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSaving}
          disabled={hasBlankLabel || hasDuplicateLabel}
          onClick={save}
          sx={{ boxShadow: 'none' }}>
          {t('save')}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};
